import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from './Logo';
interface NavLink {
  name: string;
  path: string;
  children?: {
    name: string;
    detail: string;
    path: string;
  }[];
}
const links: NavLink[] = [{
  name: 'Home',
  path: '/'
}, {
  name: 'About',
  path: '/about'
}, {
  name: 'Projects',
  path: '/projects',
  children: [{
    name: '7 Islands',
    detail: 'Resort & Marina Village · Cape Coral',
    path: '/projects/7-islands'
  }, {
    name: 'Boathouse',
    detail: 'Tiki Bar & Grill · In development',
    path: '/projects/boathouse'
  }]
}, {
  name: 'Services',
  path: '/services'
}, {
  name: 'Contact',
  path: '/contact'
}];
function isActive(pathname: string, path: string) {
  return path === '/' ? pathname === '/' : pathname.startsWith(path);
}
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const closeTimer = useRef<number>();
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsOpen(false);
    setOpenMenu(null);
  }, [location]);
  const open = (name: string) => {
    window.clearTimeout(closeTimer.current);
    setOpenMenu(name);
  };
  const scheduleClose = () => {
    closeTimer.current = window.setTimeout(() => setOpenMenu(null), 120);
  };
  return <nav className={`sticky top-0 z-50 w-full transition-[background-color,padding,box-shadow] duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-md py-2 shadow-lg' : 'bg-black py-4'}`} data-id="element-164" data-mp="34cw4is7oyxjk">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex justify-between items-center" data-id="element-165" data-mp="31b9tae6h4w0x">
        <Link to="/" className="relative z-50 inline-flex p-1.5 bg-black border border-white/80 rounded-sm" aria-label="Sinclair Development home" data-id="element-166" data-mp="318x3jeuh5hdb">
          <Logo className="h-14 md:h-[72px]" data-id="element-167" data-mp="3b35qln0qki43" />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8" data-id="element-168" data-mp="362rsoxsumojs">
          {links.map(link => {
          const active = isActive(location.pathname, link.path);
          const label = <>
                <span className={`text-sm uppercase tracking-widest font-medium transition-colors duration-300 ${active ? 'text-[#C5A55A]' : 'text-gray-300 group-hover:text-white'}`} data-id="element-169" data-mp="2dff5clev6cpf">
                  {link.name}
                </span>
                <span className={`absolute bottom-0 left-0 w-full h-[2px] bg-[#C5A55A] origin-left transition-transform duration-300 ${active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} data-id="element-170" data-mp="7it0nupfa2df" />
              </>;
          if (!link.children) {
            return <Link key={link.path} to={link.path} className="relative group py-2" data-id="element-171" data-mp="f1sms849q80h">
                  {label}
                </Link>;
          }
          const expanded = openMenu === link.name;
          return <div key={link.path} className="relative" onMouseEnter={() => open(link.name)} onMouseLeave={scheduleClose} onFocus={() => open(link.name)} onBlur={e => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setOpenMenu(null);
            }
          }} data-id="element-172" data-mp="sus0c4khps5e">
                <div className="flex items-center gap-1" data-id="element-173" data-mp="1bsl396v4622s">
                  <Link to={link.path} className="relative group py-2" data-id="element-174" data-mp="1gle7zuql1aev">
                    {label}
                  </Link>
                  <button onClick={() => setOpenMenu(expanded ? null : link.name)} aria-expanded={expanded} aria-label={`${link.name} menu`} className="p-1 text-gray-400 hover:text-white" data-id="element-175" data-mp="3p2qbrjf6obh8">
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} data-id="element-176" data-mp="3b37ghr7h0umc" />
                  </button>
                </div>

                <AnimatePresence data-id="element-177" data-mp="6p8xs6vv3eeb">
                  {expanded && <motion.div initial={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} transition={{
                duration: 0.16,
                ease: [0.23, 1, 0.32, 1]
              }} className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-72 origin-top" data-id="element-178" data-mp="1wpk79x6cj9i">
                      <ul className="bg-[#111111] border border-white/10 border-t-2 border-t-[#C5A55A] shadow-2xl py-2" data-id="element-179" data-mp="28av69zrw9xox">
                        {link.children.map(child => <li key={child.path} data-id="element-180" data-mp="2h9ypr4uz4f3y">
                            <Link to={child.path} className="block px-5 py-3 hover:bg-white/5 transition-colors duration-150" data-id="element-181" data-mp="2hccmrj97s1e5">
                              <span className={`block font-serif text-lg ${location.pathname === child.path ? 'text-[#C5A55A]' : 'text-white'}`} data-id="element-182" data-mp="2w7ise54edy6y">
                                {child.name}
                              </span>
                              <span className="block text-xs text-gray-400 mt-0.5" data-id="element-183" data-mp="351nc84l469cz">
                                {child.detail}
                              </span>
                            </Link>
                          </li>)}
                      </ul>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>

        {/* Mobile Menu Button */}
        <button className="lg:hidden text-white p-2 z-50" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu" aria-expanded={isOpen} data-id="element-184" data-mp="1omc1xnbq8rhq">
          {isOpen ? <X className="w-6 h-6" data-id="element-185" data-mp="1xkxkvjl428j1" /> : <Menu className="w-6 h-6" data-id="element-186" data-mp="1xobjqhukfzlq" />}
        </button>

        {/* Mobile Nav Overlay */}
        <AnimatePresence data-id="element-187" data-mp="1tkdhhy9y28og">
          {isOpen && <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} className="fixed inset-0 bg-black z-40 flex flex-col items-center justify-center lg:hidden" data-id="element-188" data-mp="27koc9cedk7ew">
              <div className="flex flex-col items-center gap-6" data-id="element-189" data-mp="89exkxl5fsgq">
                {links.map(link => <div key={link.path} className="flex flex-col items-center gap-3" data-id="element-190" data-mp="2jx086tt8qfan">
                    <Link to={link.path} className={`text-xl uppercase tracking-widest font-serif ${location.pathname === link.path ? 'text-[#C5A55A]' : 'text-white'}`} data-id="element-191" data-mp="2ovtlal4m84aa">
                      {link.name}
                    </Link>
                    {link.children?.map(child => <Link key={child.path} to={child.path} className={`text-base font-serif italic ${location.pathname === child.path ? 'text-[#C5A55A]' : 'text-gray-400'}`} data-id="element-192" data-mp="263iz62y6kkyv">
                        {child.name}
                      </Link>)}
                  </div>)}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </nav>;
}